<template>
  <ModalSide class="brand-modal" ref="modal" :layer="3" @close="onModalClosed">
    <Scrollbar @click.capture="onScrollbarClick">
      <div class="sticky-head">
      <header>
        <div class="header-left">
          <div class="title">{{ brand_name }}</div>
          <div class="creator-tag-dropdown" ref="tagDropdown" v-if="creator_tags.length > 1">
            <el-button :type="show_tag_menu ? 'info' : 'default'" class="tag-button" @click.stop="toggleTagMenu" :class="{ active: show_tag_menu }">
              <SvgIcon name="tag" class="icon-left" />
              <span>{{ tag_button_text }}</span>
              <SvgIcon name="arrow" class="icon-right" :class="{ rotate: show_tag_menu }" />
            </el-button>
            <div v-if="show_tag_menu" class="tag-dropdown-menu" @click.stop>
              <div class="operation-type-section">
                <label class="radio-item">
                  <input type="radio" v-model="temp_operation_type" value="or" />
                  <span>Or</span>
                </label>
                <label class="radio-item">
                  <input type="radio" v-model="temp_operation_type" value="and" />
                  <span>And</span>
                </label>
              </div>
              <div class="divider-line"></div>
              <Scrollbar class="tag-list" :show_bar="true">
                <div
                  v-for="tag in creator_tags"
                  :key="tag.id"
                  class="tag-item"
                  :class="{ disabled: tag.id === 0 && temp_operation_type === 'and' }"
                  @click="toggleTag(tag)"
                >
                  <input
                    type="checkbox"
                    :checked="tag.is_selected"
                    :disabled="tag.id === 0 && temp_operation_type === 'and'"
                    @click.stop
                    @change="toggleTag(tag)"
                  />
                  <span>{{ tag.text }}</span>
                </div>
              </Scrollbar>
              <div class="footer">
                <el-button @click="clearTags">Clear</el-button>
                <el-button type="primary" @click="applyTags">See results</el-button>
              </div>
            </div>
          </div>
        </div>
        <div class="header-right">
          <Dropdown
            :items="data_source_items"
            :direction_type="'right'"
            :icon_direction_type="'right'"
            :default_value="data_source_index"
            @update-dropdown="onDataSourceChange"
          />
          <DatePicker ref="datePickerRef" :date_range="date_range" @update-date="updateDate" />
          <div class="icon i-3" @click="close">
            <SvgIcon name="close" />
          </div>
        </div>
      </header>

      <Navi ref="navi" :menu="menu" :type="1" @selected-tab="selectTab" />
      </div>

      <AdBrandAdvertiserTable
        v-if="active_tab === 3"
        :build_url="buildAdvertiserListUrl"
        :build_statistic_url="buildAdvertiserStatisticUrl"
      />

      <AdProductTable
        v-else-if="active_tab === 2"
        search_placeholder="Search by product name"
        :build_statistic_url="buildProductsStatisticUrl"
        :build_list_url="buildProductsListUrl"
        :show_sold_by="store.state.show_advertisement"
        :start_date="moment(date_range.start_date).format('YYYY-MM-DD')"
        :end_date="moment(date_range.end_date).format('YYYY-MM-DD')"
        :brand_ids="brand_id ? [brand_id] : []"
      />

      <div class="ad-report-block" v-else-if="active_tab === 1">
        <div class="report-box" v-if="data_sta && !is_loading_data">
          <AnalyticsTopBox :data="{ title: 'Top Product Views', user: data_sta.top_view_user, icon: 'eye' }" />
          <AnalyticsTopBox :data="{ title: 'Top Clicks', user: data_sta.top_click_sum_user, icon: 'click' }" />
          <AnalyticsTopBox :data="{ title: 'Top Sales', user: data_sta.top_sales_sum_user, icon: 'pig' }" />
          <AnalyticsTopBox :data="{ title: 'Top Sales Value', user: data_sta.top_sales_value, icon: 'shopping-cart' }" />
        </div>
        <AdCreatorTable
          :build_statistic_url="buildCreatorsStatisticUrl"
          :build_list_url="buildCreatorsListUrl"
          :build_csv_url="buildCreatorsCsvUrl"
        />
      </div>

      <div v-show="active_tab === 0" class="overview-tab">
        <Loading v-if="is_loading_overview" />
        <StorefrontSummary
          v-if="is_modal_open"
          ref="storefront_summary"
          :show_date_picker="false"
          :date_range="date_range"
          :api_url="'/analytics/brand-detail-trend-detail'"
          :extra_params="`&brand_id=${brand_id}`"
          :title="summary_title"
          :selected_creator_tags="selected_creator_tags"
          :creator_tag_operation_type="tag_operation_type"
          :init_sortlink="data_source"
          :is_link_only="is_link_only"
          :no_title="true"
          :no_dropdown="true"
          :skip_auto_refetch="true"
          @update-data="onTrendDataLoaded"
        />

        <!-- Top Performing -->
        <div class="top-performing-section">
          <AnalyticsTopTable
            title="Top Performing Creators"
            :metrics="top_metrics"
            :items="top_creators"
            :active_sort="top_sort.creators"
            :loading="top_loading.creators"
            type="creator"
            @sort-change="fetchTopCreators"
            @row-click="openCreator"
          />
          <AnalyticsTopTable
            title="Top Performing Products"
            :metrics="top_metrics"
            :items="top_products"
            :active_sort="top_sort.products"
            :loading="top_loading.products"
            type="product"
            @sort-change="fetchTopProducts"
            @row-click="openProduct"
          />
          <AnalyticsTopTable
            v-if="store.state.show_advertisement"
            title="Top Performing Advertisers"
            :metrics="top_metrics"
            :items="top_advertisers"
            :active_sort="top_sort.advertisers"
            :loading="top_loading.advertisers"
            type="advertiser"
            @sort-change="fetchTopAdvertisers"
            @row-click="openAdvertiser"
          />
        </div>

        <!-- Donut Charts -->
        <div class="donut-section">
          <div class="donut-card">
            <div class="donut-card-header">
              <span class="donut-title">Operating Systems</span>
              <Dropdown
                :key="'os-' + brand_id + '-' + data_source"
                :items="os_filter_items"
                :direction_type="'right'"
                :icon_direction_type="'right'"
                :default_value="0"
                @update-dropdown="onOsFilterChange"
              />
            </div>
            <Loading class="static" v-if="is_loading_os" />
            <Doughnut v-else ref="doughnutOS" />
          </div>
          <div class="donut-card">
            <div class="donut-card-header">
              <span class="donut-title">Traffic Sources</span>
              <Dropdown
                :key="'traffic-' + brand_id + '-' + data_source"
                :items="traffic_filter_items"
                :direction_type="'right'"
                :icon_direction_type="'right'"
                :default_value="0"
                @update-dropdown="onTrafficFilterChange"
              />
            </div>
            <Loading class="static" v-if="is_loading_traffic" />
            <Doughnut v-else ref="doughnutTraffic" />
          </div>
        </div>
      </div>
    </Scrollbar>
  </ModalSide>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import ModalSide from '@/components/ui/ModalSide.vue';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import SvgIcon from '@/components/ui/SvgIcon.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import Dropdown from '@/components/ui/Dropdown.vue';
import AdBrandAdvertiserTable from '@/components/analytics/AdBrandAdvertiserTable.vue';
import AdCreatorTable from '@/components/analytics/AdCreatorTable.vue';
import AnalyticsTopBox from '@/components/analytics/AnalyticsTopBox.vue';
import AdProductTable from '@/components/analytics/AdProductTable.vue';
import AnalyticsTopTable from '@/components/analytics/AnalyticsTopTable.vue';
import Doughnut from '@/components/summary/Doughnut.vue';
import Loading from '@/components/ui/Loading.vue';
import StorefrontSummary from '@/components/summary/StorefrontSummary.vue';
import Navi from '@/components/Navi.vue';
import { handleMaxMinFun, dataSourceToApi } from '@/utils/SwitchFun.js';
import { getTags } from '@/utils/CreatorTagFun.js';
import { ElButton } from 'element-plus';

const store = useStore();
const { proxy } = getCurrentInstance();
const $ajax = proxy.$ajax;
const EventBus = proxy.EventBus;

const modal = ref(null);

const is_modal_open = ref(false);
const onModalClosed = () => {
  is_modal_open.value = false;
};
const navi = ref(null);
const storefront_summary = ref(null);
const doughnutOS = ref(null);
const doughnutTraffic = ref(null);

const is_loading_overview = ref(false);
const is_loading_os = ref(false);
const is_loading_traffic = ref(false);
const os_sort_key = ref('page_views');
const traffic_sort_key = ref('page_views');
const brand_id = ref(null);
const brand_name = ref('');
const active_tab = ref(0);
const tagDropdown = ref(null);
const show_tag_menu = ref(false);
const creator_tags = ref([]);
const selected_creator_tags = ref([]);
const data_source = ref('all');
const tag_operation_type = ref('or');
const temp_operation_type = ref('or');
const top_creators = ref([]);
const top_products = ref([]);
const data_sta = ref(null);
const is_loading_data = ref(false);
const top_advertisers = ref([]);

const date_range = reactive({
  start_date: moment().add(-30, 'days').toDate(),
  end_date: moment().add(-1, 'days').toDate(),
});
const datePickerRef = ref(null);

const data_source_items = [
  { text: 'All Data', value: 'all' },
  { text: 'Storefront Data', value: 'storefront' },
  { text: 'Shortlink Data', value: 'shortlink' },
];

const creator_type_items = [
  { text: 'All', value: '' },
  { text: 'Storefront', value: 'storefront' },
  { text: 'Shortlink', value: 'linkonly' },
];

const advertiser_filter_items = [
  { text: 'All', value: '' },
  { text: 'Creators', value: 'creator' },
  { text: 'Advertisers', value: 'advertiser' },
  { text: 'Brands', value: 'brand' },
];


const menu = computed(() => [
  { text: 'Overview' },
  { text: 'Creators' },
  { text: 'Products' },
  ...(store.state.show_advertisement ? [{ text: 'Advertisers' }] : []),
]);

const tag_button_text = computed(() => {
  if (selected_creator_tags.value.length === 0) return 'Creator Tag: All';
  return `Creator Tag: Selected(${selected_creator_tags.value.length})`;
});

const is_link_only = computed(() => store.state.user_data?.merchant_account?.platform_type === 'linkonly');

// Match Overview.vue's Traffic Source / Operating Systems filter:
// hide Views for shortlink data or link-only merchants without historical storefront tracking.
const show_views = computed(() => {
  if (data_source.value === 'shortlink') return false;
  return !is_link_only.value || (is_link_only.value && store.state.list_has_historical_storefront_tracking);
});

const doughnut_filter_items = computed(() => [
  ...(show_views.value ? [{ id: 1, text: 'Views' }] : []),
  { id: 2, text: 'Clicks' },
  { id: 3, text: 'Sales' },
  { id: 4, text: 'Sales Value' },
]);
const os_filter_items = doughnut_filter_items;
const traffic_filter_items = doughnut_filter_items;

const data_source_index = computed(() =>
  data_source_items.findIndex((i) => i.value === data_source.value)
);

const summary_title = {
  title: 'Brand Summary',
  view: 'Product Views',
  click: 'Clicks',
  sale: 'Sales',
  sale_value: 'Sales Value',
};

const selectTab = (id) => {
  const changed = id !== active_tab.value;
  active_tab.value = id;
  if (id === 0 && changed) {

    if (storefront_summary.value) {
      is_loading_overview.value = true;
      storefront_summary.value.sortlink_data = data_source.value;
      storefront_summary.value.start_date = date_range.start_date;
      storefront_summary.value.end_date = date_range.end_date;
      storefront_summary.value.getData();
    }
  } else if (id === 1 && changed) {
    // data_sta (the Creators tab's top summary cards) only renders on tab 1 —
    // fetch it lazily on switching in, not bundled with tab 0's fetches above
    fetchBrandDetailStatistic();
  }
};

const onDataSourceChange = (item) => {
  data_source.value = item.value;
  store.state.save_sortlink_data = item.value;
  resetDonutSortKeys();
  if (active_tab.value === 0) {
    // see selectTab's comment — top-performing/donuts refresh via the
    // 'update-data' cascade below, not a direct call here
    if (storefront_summary.value) {
      is_loading_overview.value = true;
      storefront_summary.value.sortlink_data = item.value;
      storefront_summary.value.chart_type = item.value === 'shortlink' ? 'click' : 'view';
      storefront_summary.value.getData();
    }
  } else {
    // data_sta (the Creators tab's top summary cards) only renders on tab 1 —
    // fetch it there instead of unconditionally alongside the tab-0 bundle above
    if (active_tab.value === 1) fetchBrandDetailStatistic();
    EventBus.emit('update-sortlink', item.value);
  }
};

const updateDate = (obj) => {
  date_range.start_date = obj.start_date;
  date_range.end_date = obj.end_date;
  if (active_tab.value === 0) {
    // see selectTab's comment — top-performing/donuts refresh via the
    // 'update-data' cascade below, not a direct call here
    if (storefront_summary.value) {
      is_loading_overview.value = true;
      storefront_summary.value.start_date = obj.start_date;
      storefront_summary.value.end_date = obj.end_date;
      storefront_summary.value.getData();
    }
  } else {
    if (active_tab.value === 1) fetchBrandDetailStatistic();
    EventBus.emit('update-date', { start_date: obj.start_date, end_date: obj.end_date });
  }
};

// StorefrontSummary emits 'update-data' once its own trend-detail fetch resolves —
// chain top-performing then donuts off that so all tab-0 fetches always fire in the
// backend-requested order: trend-detail -> top-performing -> device/source. The
// full-tab loading overlay ends here too — top-performing/donuts get their own
// section-level loading indicators from here on.
const onTrendDataLoaded = () => {
  is_loading_overview.value = false;
  fetchTopPerforming().then(() => fetchDonuts());
};

const getBase = () => {
  const s = moment(date_range.start_date).format('YYYY-MM-DD');
  const e = moment(date_range.end_date).format('YYYY-MM-DD');
  return `start_date=${s}&end_date=${e}`;
};

const getTagParam = () => {
  if (selected_creator_tags.value.length === 0) return '';
  // yaml doc: creator_tag_ids[] with style:form explode:true → each item as separate param
  const tagIds = selected_creator_tags.value.map((t) => `&creator_tag_ids[]=${t.id}`).join('');
  return `${tagIds}&operation_type=${tag_operation_type.value}`;
};

// Tier has no dropdown in this modal, so it reads the header selection straight from the store.
// No operation_type: a creator sits in exactly one tier, so picking several is always Or.
const getTierParam = () => {
  const tiers = store.state.save_selected_tiers || [];
  if (tiers.length === 0) return '';
  return tiers.map((t) => `&tier_ids[]=${t.id}`).join('');
};

watch(temp_operation_type, (val) => {
  if (val === 'and') {
    const allIdx = creator_tags.value.findIndex((t) => t.id === 0);
    if (allIdx !== -1 && creator_tags.value[allIdx].is_selected) {
      creator_tags.value[allIdx].is_selected = false;
    }
  }
});

const getMerchantId = () => store.state.user_data.merchant_account_id;

// 各 fetch 用遞增序號 guard：response 回來時序號對不上代表已有更新的請求在飛，
// 直接丟棄，避免快速切換 item / 日期 / data source 時舊 response 晚到蓋掉新資料
let statistic_seq = 0;
const fetchBrandDetailStatistic = () => {
  if (!getMerchantId() || !is_modal_open.value) return;
  const seq = ++statistic_seq;
  is_loading_data.value = true;
  const base = getBase();
  const tagParam = getTagParam() + getTierParam();
  const sortlink = dataSourceToApi(data_source.value);
  $ajax(
    `/analytics/brand-detail-statistic?${base}&brand_id=${brand_id.value}&is_include_shortlink_data=${sortlink}${tagParam}`,
    { method: 'get', withCredentials: true }
  ).then((res) => {
    if (seq !== statistic_seq) return;
    data_sta.value = res.data;
    is_loading_data.value = false;
  }).catch(() => {
    if (seq !== statistic_seq) return;
    data_sta.value = null;
    is_loading_data.value = false;
  });
};

const top_metrics = [
  { text: 'Views', key: 'views' },
  { text: 'Clicks', key: 'click' },
  { text: 'Sales', key: 'sales' },
  { text: 'Sales value', key: 'sales_value' },
  { text: 'Earnings', key: 'earnings' },
];

const top_sort = reactive({ creators: 'views', products: 'views', advertisers: 'views' });
const top_loading = reactive({ creators: false, products: false, advertisers: false });

let top_creators_seq = 0;
const fetchTopCreators = (sortKey = top_sort.creators) => {
  if (!getMerchantId() || !is_modal_open.value) return;
  const seq = ++top_creators_seq;
  top_sort.creators = sortKey;
  top_loading.creators = true;
  const base = getBase();
  const tagParam = getTagParam() + getTierParam();
  const sortlink = dataSourceToApi(data_source.value);
  return $ajax(
    `/analytics/top-performing-creators?${base}&brand_ids[]=${brand_id.value}&sort_key=${sortKey}&is_include_shortlink_data=${sortlink}${tagParam}`,
    { method: 'get', withCredentials: true }
  ).then((res) => { if (seq === top_creators_seq) top_creators.value = (res.data || []).slice(0, 7); })
   .catch(() => { if (seq === top_creators_seq) top_creators.value = []; })
   .finally(() => { if (seq === top_creators_seq) top_loading.creators = false; });
};

let top_products_seq = 0;
const fetchTopProducts = (sortKey = top_sort.products) => {
  if (!getMerchantId() || !is_modal_open.value) return;
  const seq = ++top_products_seq;
  top_sort.products = sortKey;
  top_loading.products = true;
  const base = getBase();
  const sortlink = dataSourceToApi(data_source.value);
  // TODO: 確認 /analytics/top-performing-products endpoint 是否存在（yaml doc 未記載）
  return $ajax(
    `/analytics/top-performing-products?${base}&brand_ids[]=${brand_id.value}&sort_key=${sortKey}&is_include_shortlink_data=${sortlink}`,
    { method: 'get', withCredentials: true }
  ).then((res) => {
    if (seq !== top_products_seq) return;
    const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
    top_products.value = list.slice(0, 7).map(p => ({ ...p, name: p.product_name, image_url: p.product_image }));
  })
   .catch(() => { if (seq === top_products_seq) top_products.value = []; })
   .finally(() => { if (seq === top_products_seq) top_loading.products = false; });
};

let top_advertisers_seq = 0;
const fetchTopAdvertisers = (sortKey = top_sort.advertisers) => {
  if (!getMerchantId() || !is_modal_open.value) return;
  const seq = ++top_advertisers_seq;
  top_sort.advertisers = sortKey;
  top_loading.advertisers = true;
  const base = getBase();
  const sortlink = dataSourceToApi(data_source.value);
  const tagParam = getTagParam() + getTierParam();
  $ajax(
    `/analytics/top-performing-advertisers?${base}&brand_ids[]=${brand_id.value}&sort_key=${sortKey}&is_include_shortlink_data=${sortlink}${tagParam}`,
    { method: 'get', withCredentials: true }
  ).then((res) => {
    if (seq !== top_advertisers_seq) return;
    const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
    top_advertisers.value = list.slice(0, 7);
  })
   .catch(() => { if (seq === top_advertisers_seq) top_advertisers.value = []; })
   .finally(() => { if (seq === top_advertisers_seq) top_loading.advertisers = false; });
};

const fetchTopPerforming = () => {
  // the Top Performing Advertisers table only renders when show_advertisement is on
  // (see the AnalyticsTopTable v-if above) — skip the fetch otherwise
  return Promise.all([
    fetchTopCreators(),
    fetchTopProducts(),
    store.state.show_advertisement ? fetchTopAdvertisers() : null,
  ]);
};

// dropdown option id -> API sort_key (matches Overview.vue's doughnut filter)
const doughnutSortKey = (id) => {
  switch (id) {
    case 1: return 'page_views';
    case 2: return 'click';
    case 3: return 'sales';
    case 4: return 'sales_value';
    default: return show_views.value ? 'page_views' : 'click';
  }
};

// On data-source change, reset both donuts' metric to the first available option (in sync with the dropdown reset)
const resetDonutSortKeys = () => {
  const defaultKey = show_views.value ? 'page_views' : 'click';
  os_sort_key.value = defaultKey;
  traffic_sort_key.value = defaultKey;
};

// The break-down API returns single-letter abbreviations for `name` under sales / sales_value etc. (e.g. i / o / t);
// as in Overview.vue, expand them to full names by first letter (source uses sourceList, device-use uses deviceList)
const sourceList = ['instagram', 'tiktok', 'youtube', 'motom', 'search', 'other', 'linktree', 'beacons'];
const deviceList = ['android', 'ios', 'windows', 'others'];
const expandDonutNames = (rows, list) =>
  (rows || []).map((e) => {
    let index = -1;
    if (e.name && e.name.length === 1) {
      index = list.findIndex((ee) => ee[0] === e.name);
    }
    return { ...e, name: index !== -1 ? list[index] : e.name };
  });

let os_seq = 0;
const fetchDonutOS = () => {
  if (!getMerchantId() || !is_modal_open.value) return;
  const seq = ++os_seq;
  is_loading_os.value = true;
  const base = getBase();
  const sortlink = dataSourceToApi(data_source.value);
  $ajax(
    `/analytics/break-down/device-use?${base}&brand_ids[]=${brand_id.value}&sort_key=${os_sort_key.value}&is_include_shortlink_data=${sortlink}${getTagParam()}${getTierParam()}`,
    { method: 'get', withCredentials: true }
  ).then((res) => {
    if (seq !== os_seq) return;
    is_loading_os.value = false;
    nextTick(() => {
      if (seq === os_seq) doughnutOS.value?.update({ type: os_sort_key.value, data: expandDonutNames(res.data.data, deviceList) });
    });
  }).catch(() => { if (seq === os_seq) is_loading_os.value = false; });
};

let traffic_seq = 0;
const fetchDonutTraffic = () => {
  if (!getMerchantId() || !is_modal_open.value) return;
  const seq = ++traffic_seq;
  is_loading_traffic.value = true;
  const base = getBase();
  const sortlink = dataSourceToApi(data_source.value);
  $ajax(
    `/analytics/break-down/source?${base}&brand_ids[]=${brand_id.value}&sort_key=${traffic_sort_key.value}&is_include_shortlink_data=${sortlink}${getTagParam()}${getTierParam()}`,
    { method: 'get', withCredentials: true }
  ).then((res) => {
    if (seq !== traffic_seq) return;
    is_loading_traffic.value = false;
    nextTick(() => {
      if (seq === traffic_seq) doughnutTraffic.value?.update({ type: traffic_sort_key.value, data: expandDonutNames(res.data.data, sourceList) });
    });
  }).catch(() => { if (seq === traffic_seq) is_loading_traffic.value = false; });
};

const onOsFilterChange = (item) => {
  os_sort_key.value = doughnutSortKey(item?.id);
  fetchDonutOS();
};

const onTrafficFilterChange = (item) => {
  traffic_sort_key.value = doughnutSortKey(item?.id);
  fetchDonutTraffic();
};

const fetchDonuts = () => {
  return Promise.all([fetchDonutOS(), fetchDonutTraffic()]);
};

// 對齊 Products.vue / Brands.vue 模式 — buildUrl 不傳 merchant_account_id（server 從 JWT 推斷）

// BDP — buildUrl 全部用 brand_ids= scope

// BDP Advertisers tab
const buildAdvertiserListUrl = ({ base, sortlink, page, per_page, sort_key, sort_val, keyword }) =>
  `/analytics/brand-detail-advertiser-list?${base}&brand_id=${brand_id.value}&is_include_shortlink_data=${sortlink}&page=${page}&per_page=${per_page}${sort_key ? `&sort_key=${sort_key}&sort_val=${sort_val}` : ''}&keyword=${keyword || ''}${getTagParam()}${getTierParam()}`;

const buildAdvertiserStatisticUrl = ({ base, sortlink, curated }) =>
  `/analytics/brand-analytics-statistic?${base}&brand_ids[]=${brand_id.value}&is_include_shortlink_data=${sortlink}&is_include_curation_data=${curated || 0}${getTagParam()}${getTierParam()}`;

// BDP Products tab
const buildProductsStatisticUrl = ({ base, sortlink, keyword, curated, sold_by }) =>
  `/analytics/merchant-account-statistic?${base}&brand_ids[]=${brand_id.value}&is_include_shortlink_data=${sortlink}&product_keyword=${keyword}&is_include_curation_data=${curated}${sold_by ? `&sold_by=${sold_by}` : ''}${getTagParam()}${getTierParam()}`;

const buildProductsListUrl = ({ base, page, per_page, sort_key, sort_val, sortlink, keyword, curated, sold_by }) =>
  `/creator/product-list?${base}&brand_ids[]=${brand_id.value}&page=${page}&per_page=${per_page}&sort_key=${sort_key}&sort_val=${sort_val}&is_include_shortlink_data=${sortlink}&product_keyword=${keyword}&is_include_curation_data=${curated}${sold_by ? `&sold_by=${sold_by}` : ''}${getTagParam()}${getTierParam()}`;

// BDP Creators tab — statistic
const buildCreatorsStatisticUrl = ({ base, sortlink, curated, creator_type }) =>
  `/analytics/brand-detail-statistic?${base}&brand_id=${brand_id.value}&is_include_shortlink_data=${sortlink}&is_include_curation_data=${curated}${creator_type ? `&platform_type=${creator_type}` : ''}${getTagParam()}${getTierParam()}`;

const buildCreatorsListUrl = ({ base, page, per_page, sort_key, sort_val, sortlink, keyword, curated, creator_type }) =>
  `/analytics/brand-performance-by-creators?${base}&brand_id=${brand_id.value}&page=${page}&per_page=${per_page}&sort_key=${sort_key}&sort_val=${sort_val}&is_include_shortlink_data=${sortlink}&is_include_curation_data=${curated}${keyword ? `&keyword=${keyword}` : ''}${creator_type ? `&platform_type=${creator_type}` : ''}${getTagParam()}${getTierParam()}`;

const buildCreatorsCsvUrl = ({ base, page, per_page, sort_key, sort_val, sortlink, keyword, curated, creator_type }) =>
  `/analytics/brand-performance-by-creators-csv-file?${base}&brand_id=${brand_id.value}&page=${page}&per_page=${per_page}&sort_key=${sort_key}&sort_val=${sort_val}&is_include_shortlink_data=${sortlink}&is_include_curation_data=${curated}${keyword ? `&keyword=${keyword}` : ''}${creator_type ? `&platform_type=${creator_type}` : ''}${getTagParam()}${getTierParam()}`;

const toggleTagMenu = () => {
  show_tag_menu.value = !show_tag_menu.value;
};

const onScrollbarClick = (e) => {
  // Clicks inside the tag menu must not close it: the tag menu itself listens for close-dropdown-tags
  // (see onMounted), so any button inside would wrongly close it. Skip all close logic for clicks inside the tag dropdown.
  const insideTag = tagDropdown.value && tagDropdown.value.contains(e.target);
  if (!insideTag && !e.target.closest('.ui-dropdown')) {
    EventBus.emit('close-dropdown-tags', '');
  }
  if (!show_tag_menu.value) return;
  if (insideTag) return;
  show_tag_menu.value = false;
};

const toggleTag = (tag) => {
  if (tag.id === 0 && temp_operation_type.value === 'and') return;
  if (tag.id === 0) {
    creator_tags.value = creator_tags.value.map((t) => ({ ...t, is_selected: t.id === 0 }));
    selected_creator_tags.value = [];
  } else {
    const idx = creator_tags.value.findIndex((t) => t.id === tag.id);
    if (idx !== -1) creator_tags.value[idx].is_selected = !creator_tags.value[idx].is_selected;
    selected_creator_tags.value = creator_tags.value.filter((t) => t.id !== 0 && t.is_selected);
    if (temp_operation_type.value === 'or') {
      const allIdx = creator_tags.value.findIndex((t) => t.id === 0);
      if (allIdx !== -1) creator_tags.value[allIdx].is_selected = selected_creator_tags.value.length === 0;
    }
  }
};

const clearTags = () => {
  creator_tags.value = creator_tags.value.map((t) => ({ ...t, is_selected: t.id === 0 }));
  selected_creator_tags.value = [];
  temp_operation_type.value = 'or';
};

const applyTags = () => {
  show_tag_menu.value = false;
  tag_operation_type.value = temp_operation_type.value;
  if (active_tab.value === 0) {
    // see selectTab's comment — top-performing/donuts refresh via the
    // 'update-data' cascade, not a direct call here
    if (storefront_summary.value) {
      is_loading_overview.value = true;
      storefront_summary.value.getData();
    }
  } else {
    if (active_tab.value === 1) fetchBrandDetailStatistic();
    EventBus.emit('update-date', { start_date: date_range.start_date, end_date: date_range.end_date });
  }
};

// Apply the creator-tag selection chosen in the parent page header (persisted in the store),
// so opening the modal inherits the tags already selected on the Brands page.
const applySavedTagSelection = () => {
  const savedTags = store.state.save_selected_tags || [];
  selected_creator_tags.value = [...savedTags];
  tag_operation_type.value = store.state.save_tag_operation_type || 'or';
  temp_operation_type.value = tag_operation_type.value;
  if (creator_tags.value.length) {
    const savedIds = savedTags.map((t) => t.id);
    creator_tags.value = creator_tags.value.map((t) => ({
      ...t,
      is_selected: savedIds.length ? savedIds.includes(t.id) : t.id === 0,
    }));
  }
};

const loadCreatorTags = async () => {
  try {
    const tagsData = await getTags(proxy);
    if (tagsData && tagsData.data && tagsData.data.length > 0) {
      creator_tags.value = [
        { id: 0, text: 'All Tags', is_selected: true },
        ...tagsData.data.map((t) => ({ ...t, is_selected: false })),
      ];
      applySavedTagSelection();
    }
  } catch (e) {
    creator_tags.value = [];
  }
};

const open = (item) => {
  is_modal_open.value = true;
  active_tab.value = 0;
  navi.value?.selectIndex(0);
  data_source.value = store.state.save_sortlink_data || 'all';
  top_creators.value = [];
  top_products.value = [];
  top_advertisers.value = [];
  data_sta.value = null;
  resetDonutSortKeys();
  // Inherit the tags selected in the parent header before the fetches below read them via getTagParam()
  applySavedTagSelection();
  if (creator_tags.value.length === 0) loadCreatorTags();

  if (item?.start_date && item?.end_date) {
    date_range.start_date = item.start_date;
    date_range.end_date = item.end_date;
  } else if (store.state.save_analytics_start_date && store.state.save_analytics_end_date) {
    date_range.start_date = store.state.save_analytics_start_date;
    date_range.end_date = store.state.save_analytics_end_date;
  }
  const checked = handleMaxMinFun(date_range.start_date, date_range.end_date, proxy);
  date_range.start_date = checked[0];
  date_range.end_date = checked[1];
  datePickerRef.value?.handleOutsideDate();

  // data_sta (fetchBrandDetailStatistic) isn't fetched here — it only feeds the
  // Creators tab's (tab 1) summary cards, and open() always lands on tab 0;
  // selectTab(1) fetches it lazily once the user actually switches there.
  // fetchTopPerforming/fetchDonuts aren't called directly either — on the very first
  // open, StorefrontSummary's own mounted() hook fires its trend-detail fetch and its
  // 'update-data' emit cascades into them (onTrendDataLoaded); on later opens it's
  // already mounted, so it needs an explicit nudge here to refetch for the (possibly
  // new) brand — either way the emit is what actually triggers the rest, keeping the
  // backend-requested order (trend-detail -> top-performing -> device/source) instead
  // of firing everything in parallel.
  is_loading_overview.value = true;
  if (storefront_summary.value) {
    storefront_summary.value.sortlink_data = data_source.value;
    storefront_summary.value.start_date = date_range.start_date;
    storefront_summary.value.end_date = date_range.end_date;
    
    nextTick(() => {
      if (is_modal_open.value) storefront_summary.value?.getData();
    });
  }
  modal.value.open();
};

const openCreator = (item) => {
  EventBus.emit('open-creator-modal', {
    user_id: item.id,
    start_date: moment(date_range.start_date).format('YYYY-MM-DD'),
    end_date: moment(date_range.end_date).format('YYYY-MM-DD'),
  });
};

const openProduct = (item) => {
  EventBus.emit('open-product-modal', {
    product_id: item.id ?? item.product_id,
    start_date: moment(date_range.start_date).format('YYYY-MM-DD'),
    end_date: moment(date_range.end_date).format('YYYY-MM-DD'),
    init_sortlink: data_source.value,
  });
};

const openAdvertiser = (item) => {
  EventBus.emit('open-advertiser-detail', {
    id: item.advertiser_id,
    name: item.display_name || item.name,
    start_date: moment(date_range.start_date).format('YYYY-MM-DD'),
    end_date: moment(date_range.end_date).format('YYYY-MM-DD'),
  });
};

const close = () => {
  is_modal_open.value = false;
  modal.value.close();
};

onMounted(() => {
  EventBus.off('open-brand-detail');
  EventBus.on('open-brand-detail', (item) => {
    brand_id.value = item.id || item.brand_id;
    brand_name.value = item.name || item.brand_name || '(Brand Name)';
    open(item);
  });
  EventBus.on('open-advertiser-detail', close);
  EventBus.on('close-dropdown-tags', () => { show_tag_menu.value = false; });
});

onBeforeUnmount(() => {
  EventBus.off('open-advertiser-detail', close);
  EventBus.off('close-dropdown-tags');
});
</script>

<style lang="scss">
.brand-modal {
  width: 100%;
  height: 100%;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1002 !important;
  
  .ui-modal-side--container{
    padding: 24px;
  }

  .sticky-head {
    position: sticky;
    top: 0;
    z-index: 30;
    background-color: var(--bg-01);
  }

  .storefront-summary{
    .direction--left{
      right: 0px;
    }
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    gap: 12px;

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      min-width: 0;

      .title {
        font: var(--font-demi-20);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;

      .icon.i-3 {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: 8px;

        > svg {
          width: 14px;
          height: 14px;
          fill: var(--black-75);
        }

        &:hover > svg {
          fill: var(--dark-blue);
        }
      }
    }
  }

  .creator-tag-dropdown {
    position: relative;
    z-index: 200;

    .tag-button {
      min-width: 172px;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 0 12px;
      border-radius: 6px !important;
      height: 32px !important;
      line-height: 32px !important;
      color: var(--black-60) !important;
      cursor: pointer;

      & > span {
        gap: 4px;
      }

      .icon-left { width: 16px; height: 16px; fill: var(--black-60); flex-shrink: 0; }

      span { flex: 1; text-align: left; }

      .icon-right {
        width: 12px; height: 12px; fill: var(--black-60);
        transition: transform 0.2s; flex-shrink: 0;
        &.rotate { transform: rotate(180deg); }
      }
    }

    .tag-dropdown-menu {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      min-width: 240px;
      background: white;
      border: 1px solid var(--black-20);
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      z-index: 200;

      .operation-type-section {
        padding: 12px 20px;
        display: flex;
        gap: 24px;

        .radio-item {
          display: flex;
          align-items: center;
          cursor: pointer;

          input[type='radio'] {
            margin: 0 8px 0 0;
            width: 12px;
            height: 12px;
            accent-color: var(--dark-blue);
          }

          span { font: var(--font-fg-12); color: var(--black-90); user-select: none; }
        }
      }

      .divider-line { height: 1px; background-color: var(--black-20); }

      .tag-list {
        max-height: 300px;
        padding: 8px 0;

        .tag-item {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          cursor: pointer;
          transition: background-color 0.2s;

          &:hover { background-color: var(--black-10); }

          input[type='checkbox'] {
            margin-right: 12px;
            cursor: pointer;
            width: 12px;
            height: 12px;
            -webkit-appearance: none;
            appearance: none;
            border: 1px solid var(--black-50);
            border-radius: 50%;
            background-color: white;
            vertical-align: middle;
            flex-shrink: 0;

            &:checked {
              background-color: var(--dark-blue);
              border-color: var(--dark-blue);
              position: relative;

              &::after {
                content: '\2713';
                position: absolute;
                color: white;
                font-size: 10px;
                font-weight: bold;
                top: -1px;
                left: 1px;
              }
            }

            &:hover:not(:disabled) { border-color: var(--black-75); }
            &:disabled { opacity: 0.5; cursor: not-allowed; }
          }

          span { font: var(--font-fg-12); color: var(--black-90); user-select: none; }

          &.disabled { opacity: 0.5; cursor: not-allowed; pointer-events: none; }
        }
      }

      .footer {
        display: flex;
        gap: 12px;
        padding: 16px 20px;
        justify-content: flex-end;
        border-top: 1px solid var(--black-10);

        .el-button { height: 28px; font-size: 12px; padding: 0 16px; border-radius: 6px; }
      }
    }
  }

  .ad-report-block{
    .report-box{
      margin-top: 16px;
    }
  }


  .overview-tab {
    padding: 0;
    margin-top: 16px;

    .top-performing-section {
      display: flex;
      padding: 16px 0;
      gap: 12px;

      .top-table {
        flex: 1;
        min-width: 0;
      }
    }

    .donut-section {
      display: flex;
      gap: 12px;
      padding: 0 0px 16px;

      .donut-card {
        flex: 1;
        border: solid 1px var(--border);
        border-radius: 8px;
        padding:16px;

        .donut-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;

          .donut-title { font: var(--font-demi-20); color: var(--black-90); }

        }
      }
    }
  }
}
</style>
